import React, {useState, useEffect} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import {useApp} from '../context/AppContext'

const {FiPlus, FiTrash2, FiSave, FiBook, FiCalendar, FiClock, FiInfo, FiX, FiTarget} = FiIcons

const AssessmentPlan = () => {
  const {state, dispatch, assessmentService, courseService} = useApp()
  const [assessments, setAssessments] = useState([])
  const [cloStatements, setCloStatements] = useState({CLO1: 'Understand basic concepts', CLO2: 'Apply theoretical knowledge', CLO3: 'Analyze complex problems'})
  const [loading, setLoading] = useState(false)
  const [showCLOGuide, setShowCLOGuide] = useState(false)
  const [showAlignmentGuide, setShowAlignmentGuide] = useState(false)

  useEffect(() => {
    if (state.activeCourse) {
      loadCourseData()
    } else {
      setAssessments([
        {id: 1, type: 'Continuous Assessment', name: 'Quiz 1', marks: 10, sltCI: 2, clo: 'CLO1'},
        {id: 2, type: 'Continuous Assessment', name: 'Assignment 1', marks: 15, sltCI: 8, clo: 'CLO2'},
        {id: 3, type: 'Final Assessment', name: 'Final Exam', marks: 60, sltCI: 3, clo: 'CLO3'}
      ])
    }
  }, [state.activeCourse])

  const loadCourseData = async () => {
    if (!state.activeCourse) return
    setLoading(true)
    try {
      const [assessmentsResult, cloResult] = await Promise.all([
        assessmentService.getAssessments(state.activeCourse.id),
        assessmentService.getCLOStatements(state.activeCourse.id)
      ])

      if (assessmentsResult.data && assessmentsResult.data.length > 0) {
        const formattedAssessments = assessmentsResult.data.map(a => ({
          id: a.id,
          type: a.assessment_type,
          name: a.assessment_name,
          marks: a.marks,
          sltCI: parseFloat(a.slt_ci),
          clo: a.clo_mapping
        }))
        setAssessments(formattedAssessments)
      }

      if (cloResult.data && cloResult.data.length > 0) {
        const cloObj = cloResult.data.reduce((acc, clo) => {
          acc[clo.clo_code] = clo.statement
          return acc
        }, {})
        setCloStatements(cloObj)
      }
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message})
    } finally {
      setLoading(false)
    }
  }

  const addAssessment = () => {
    const newAssessment = {
      id: Date.now(),
      type: 'Continuous Assessment',
      name: '',
      marks: 0,
      sltCI: 0,
      clo: 'CLO1'
    }
    setAssessments([...assessments, newAssessment])
  }

  const updateAssessment = (id, field, value) => {
    setAssessments(assessments.map(assessment => 
      assessment.id === id ? {...assessment, [field]: value} : assessment
    ))
  }

  const removeAssessment = (id) => {
    setAssessments(assessments.filter(assessment => assessment.id !== id))
  }

  const updateCLOStatement = (clo, statement) => {
    setCloStatements({...cloStatements, [clo]: statement})
  }

  const addCLOStatement = () => {
    const cloKeys = Object.keys(cloStatements)
    const newCLONumber = cloKeys.length + 1
    const newCLOKey = `CLO${newCLONumber}`
    setCloStatements({...cloStatements, [newCLOKey]: ''})
  }

  const removeCLOStatement = (cloToRemove) => {
    // Don't allow removal if only one CLO remains
    if (Object.keys(cloStatements).length <= 1) {
      alert('At least one CLO statement is required.')
      return
    }
    
    // Check if this CLO is being used in any assessment
    const isUsed = assessments.some(assessment => assessment.clo === cloToRemove)
    if (isUsed) {
      alert(`Cannot remove ${cloToRemove} as it is mapped to one or more assessments. Please update those assessments first.`)
      return
    }
    
    const newCLOStatements = {...cloStatements}
    delete newCLOStatements[cloToRemove]
    setCloStatements(newCLOStatements)
  }

  const saveData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first')
      return
    }
    setLoading(true)
    try {
      console.log('===SAVING ASSESSMENT PLAN AND CLO STATEMENTS===')
      // Save assessments with proper synchronization
      const {error: assessmentError} = await assessmentService.saveAssessmentPlan(
        state.activeCourse.id,
        assessments
      )
      if (assessmentError) throw new Error(assessmentError)

      // Save CLO statements
      const {error: cloError} = await assessmentService.saveCLOStatements(
        state.activeCourse.id,
        cloStatements
      )
      if (cloError) throw new Error(cloError)

      // Clean up any orphaned marks that might reference old assessment IDs
      await assessmentService.cleanupOrphanedMarks(state.activeCourse.id)

      // Update course progress
      const progress = calculateProgress()
      const {error: progressError} = await courseService.updateCourseProgress(
        state.activeCourse.id,
        progress
      )
      if (progressError) throw new Error(progressError)

      // Reload the data to get the correct IDs
      await loadCourseData()

      // Update local state
      dispatch({type: 'SET_ASSESSMENT_PLAN', payload: assessments})
      dispatch({type: 'SET_CLO_STATEMENTS', payload: cloStatements})
      dispatch({type: 'UPDATE_COURSE_PROGRESS', payload: {courseId: state.activeCourse.id, progress}})

      alert('Assessment plan saved successfully!')
    } catch (error) {
      dispatch({type: 'SET_ERROR', payload: error.message})
      alert('Error saving assessment plan: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const calculateProgress = () => {
    const totalMarks = assessments.reduce((sum, assessment) => sum + assessment.marks, 0)
    const hasValidAssessments = assessments.length > 0 && assessments.every(a => a.name && a.marks > 0)
    const closFilled = Object.values(cloStatements).every(statement => statement.trim() !== '')
    
    let progress = 0
    if (hasValidAssessments) progress += 50
    if (totalMarks === 100) progress += 25
    if (closFilled) progress += 25
    
    return progress
  }

  const totalMarks = assessments.reduce((sum, assessment) => sum + assessment.marks, 0)

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to start working on the assessment plan.</p>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="space-y-6"
    >
      {/* Course Info Header - Enhanced */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h2 className="text-xl font-bold text-blue-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-700">Faculty:</span>
            <p className="text-blue-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-blue-700">Programme:</span>
            <p className="text-blue-600">{state.activeCourse.programme}</p>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-blue-700" />
            <div>
              <span className="font-medium text-blue-700">Semester:</span>
              <p className="text-blue-600">{state.activeCourse.semester}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-blue-700" />
            <div>
              <span className="font-medium text-blue-700">Session:</span>
              <p className="text-blue-600">{state.activeCourse.session}</p>
            </div>
          </div>
          <div>
            <span className="font-medium text-blue-700">Credit Hours:</span>
            <p className="text-blue-600">{state.activeCourse.credit_hours || state.activeCourse.creditHours}</p>
          </div>
          <div>
            <span className="font-medium text-blue-700">Progress:</span>
            <p className="text-blue-600">{state.activeCourse.progress || 0}%</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Table 1: Assessment Plan</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAlignmentGuide(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiTarget} className="w-4 h-4" />
              <span>Constructive Alignment</span>
            </button>
            <button
              onClick={() => setShowCLOGuide(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span>CLO Guide</span>
            </button>
          </div>
        </div>

        {/* CLO Statements Section with Add/Remove Functionality */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">CLO Statements</h3>
            <div className="flex space-x-2">
              <button
                onClick={addCLOStatement}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3" />
                <span>Add CLO</span>
              </button>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(cloStatements).map(([clo, statement]) => (
              <div key={clo} className="flex items-center space-x-4">
                <label className="w-16 text-sm font-medium text-gray-700">{clo}:</label>
                <input
                  type="text"
                  value={statement}
                  onChange={(e) => updateCLOStatement(clo, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter CLO statement"
                />
                <button
                  onClick={() => removeCLOStatement(clo)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={`Remove ${clo}`}
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-600 italic">
            Add or remove Course Learning Outcomes (CLOs) according to your course needs.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">Assessment Type</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Assessment Name</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Marks (%)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">SLT CI (Hours)</th>
                <th className="border border-gray-300 px-4 py-2 text-left">CLO Mapping</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((assessment) => (
                <tr key={assessment.id}>
                  <td className="border border-gray-300 px-4 py-2">
                    <select
                      value={assessment.type}
                      onChange={(e) => updateAssessment(assessment.id, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Continuous Assessment">Continuous Assessment</option>
                      <option value="Final Assessment">Final Assessment</option>
                    </select>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={assessment.name}
                      onChange={(e) => updateAssessment(assessment.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Assessment name"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="number"
                      value={assessment.marks}
                      onChange={(e) => updateAssessment(assessment.id, 'marks', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                      max="100"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="number"
                      value={assessment.sltCI}
                      onChange={(e) => updateAssessment(assessment.id, 'sltCI', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.5"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <select
                      value={assessment.clo}
                      onChange={(e) => updateAssessment(assessment.id, 'clo', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.keys(cloStatements).map(clo => (
                        <option key={clo} value={clo}>{clo}</option>
                      ))}
                    </select>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      onClick={() => removeAssessment(assessment.id)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td colSpan="2" className="border border-gray-300 px-4 py-2 font-semibold">Total</td>
                <td className="border border-gray-300 px-4 py-2 font-semibold">{totalMarks}%</td>
                <td colSpan="3" className="border border-gray-300 px-4 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mt-6 flex justify-between">
          <button
            onClick={addAssessment}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Assessment</span>
          </button>
          <button
            onClick={saveData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{loading ? 'Saving...' : 'Save Assessment Plan'}</span>
          </button>
        </div>

        {totalMarks !== 100 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">
              <strong>Warning:</strong> Total marks should equal 100%. Current total: {totalMarks}%
            </p>
          </div>
        )}

        {/* Important Notice */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">🔄 Synchronization Notice:</h4>
          <p className="text-blue-800 text-sm">
            When you save changes to the Assessment Plan, the system automatically:
          </p>
          <ul className="text-blue-800 text-sm mt-2 space-y-1 list-disc list-inside">
            <li>Preserves existing student marks where possible</li>
            <li>Updates assessment details (names, marks, CLO mappings)</li>
            <li>Removes marks for deleted assessments</li>
            <li>Maintains data consistency across all sections</li>
          </ul>
        </div>
      </div>

      {/* Constructive Alignment Modal */}
      <AnimatePresence>
        {showAlignmentGuide && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAlignmentGuide(false)}
          >
            <motion.div
              initial={{scale: 0.95, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.95, opacity: 0}}
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Constructive Alignment in Curriculum Design</h2>
                <button
                  onClick={() => setShowAlignmentGuide(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Introduction to Constructive Alignment */}
                <section className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">What is Constructive Alignment?</h3>
                  <p className="text-green-800 text-sm mb-3">
                    Constructive alignment is an approach to curriculum design where all aspects of teaching and assessment are tuned to support and encourage a higher-order learning process. The key principle is that the components in the teaching system, especially the teaching methods used and the assessment tasks, are aligned with the learning activities assumed in the intended outcomes.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-green-200 mt-3">
                    <h4 className="font-semibold text-green-800 mb-2">Two Key Aspects:</h4>
                    <div className="space-y-2">
                      <p className="text-green-700 text-sm">
                        <strong>1. Constructive:</strong> Students construct meaning through relevant learning activities. It is something learners have to create for themselves.
                      </p>
                      <p className="text-green-700 text-sm">
                        <strong>2. Alignment:</strong> What the teacher does, which is to set up a learning environment that supports the learning activities appropriate to achieving the desired learning outcomes.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Biggs' Framework */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Biggs' Framework of Constructive Alignment</h3>
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold text-indigo-800 mb-3">The Process Involves:</h4>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="text-indigo-700 text-sm">
                          <strong className="text-indigo-900">Step 1:</strong> Identify the intended learning outcomes
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="text-indigo-700 text-sm">
                          <strong className="text-indigo-900">Step 2:</strong> Design assessment tasks to measure the attainment of the learning outcomes
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="text-indigo-700 text-sm">
                          <strong className="text-indigo-900">Step 3:</strong> Plan learning activities to enable students to develop the skills, knowledge and understanding described in the intended learning outcomes and measured by assessments
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="text-indigo-700 text-sm">
                          <strong className="text-indigo-900">Step 4:</strong> Choose the content (topics/examples/resources/materials) required to support the learning activities
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Elements of Constructive Alignment Process */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Elements of the Constructive Alignment Process</h3>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-blue-800 text-sm mb-3">
                      The constructive alignment process involves the following elements that need to be aligned with and mapped accordingly:
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            A targeted MQF learning outcome cluster (LOC) or subcluster
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            A PLO, that is mapped to the MQF LOC in (1)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">3</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            A CLO, that is mapped to the PLO in (2)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">4</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            Formative and summative assessment items, that are mapped to the CLO in (3)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">5</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            Learning activities, that are mapped to match the assessment items in (4)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">6</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            Weightage of summative assessments, that are determined to loosely correlate to the amount of learning and assessment activities in (5) and (4)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-blue-200 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">7</div>
                        <div className="bg-white p-3 rounded-lg border border-blue-200 flex-grow">
                          <p className="text-blue-700 text-sm">
                            SLT of the combined assessment and learning activities is determined to verify the weightage in (6)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Visual Representation */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Visual Representation of Constructive Alignment</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="relative py-8">
                      <div className="flex flex-col items-center mb-8">
                        <div className="bg-blue-600 text-white rounded-lg py-3 px-6 text-center mb-2 w-64">
                          <p className="font-semibold">Intended Learning Outcomes</p>
                          <p className="text-xs mt-1">(What students should be able to do)</p>
                        </div>
                        <div className="h-8 border-l-2 border-blue-600"></div>
                      </div>
                      
                      <div className="flex justify-center gap-20 mb-8">
                        <div className="flex flex-col items-center">
                          <div className="bg-green-600 text-white rounded-lg py-3 px-6 text-center mb-2 w-64">
                            <p className="font-semibold">Teaching & Learning Activities</p>
                            <p className="text-xs mt-1">(What the teacher does)</p>
                          </div>
                          <div className="h-8 border-l-2 border-green-600"></div>
                        </div>
                        
                        <div className="flex flex-col items-center">
                          <div className="bg-orange-600 text-white rounded-lg py-3 px-6 text-center mb-2 w-64">
                            <p className="font-semibold">Assessment Tasks</p>
                            <p className="text-xs mt-1">(How we measure achievement)</p>
                          </div>
                          <div className="h-8 border-l-2 border-orange-600"></div>
                        </div>
                      </div>
                      
                      <div className="flex justify-center">
                        <div className="bg-purple-600 text-white rounded-lg py-3 px-6 text-center w-64">
                          <p className="font-semibold">Student Learning</p>
                          <p className="text-xs mt-1">(What the student constructs)</p>
                        </div>
                      </div>
                      
                      {/* Connecting arrows */}
                      <div className="absolute top-[88px] left-1/2 transform -translate-x-1/2 w-3/4 border-t-2 border-gray-400 border-dashed"></div>
                      
                      {/* Side labels */}
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gray-200 rounded-lg p-2 text-sm">
                        <p className="text-gray-700 font-medium">Teacher-centered activities</p>
                      </div>
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-gray-200 rounded-lg p-2 text-sm">
                        <p className="text-gray-700 font-medium">Student learning experiences</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Practical Example */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Practical Example of Constructive Alignment</h3>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-3">Example: Data Structures Course</h4>
                    <div className="space-y-4">
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <p className="text-yellow-800 font-medium">Intended Learning Outcome (CLO):</p>
                        <p className="text-yellow-700 text-sm mt-1">
                          "By the end of this course, students should be able to <strong>analyze</strong> the efficiency of algorithms using appropriate complexity measures."
                        </p>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <p className="text-yellow-800 font-medium">Teaching & Learning Activities:</p>
                        <ul className="text-yellow-700 text-sm mt-1 space-y-1 list-disc list-inside">
                          <li>Lectures on algorithm complexity analysis</li>
                          <li>Tutorial sessions where students practice analyzing algorithms</li>
                          <li>Lab exercises comparing different algorithm implementations</li>
                          <li>Case studies of real-world algorithm optimization</li>
                        </ul>
                      </div>
                      
                      <div className="bg-white p-3 rounded-lg border border-yellow-200">
                        <p className="text-yellow-800 font-medium">Assessment Tasks:</p>
                        <ul className="text-yellow-700 text-sm mt-1 space-y-1 list-disc list-inside">
                          <li>Assignment: Analyze the time and space complexity of given algorithms (15%)</li>
                          <li>Quiz: Identify the complexity class of various algorithms (10%)</li>
                          <li>Project: Implement and analyze the efficiency of different sorting algorithms (25%)</li>
                          <li>Final Exam: Questions requiring algorithm analysis (20% of exam)</li>
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 bg-yellow-100 p-3 rounded-lg border border-yellow-300">
                      <p className="text-yellow-800 text-sm font-medium">Key Alignment Points:</p>
                      <ul className="text-yellow-800 text-sm mt-1 space-y-1 list-disc list-inside">
                        <li>The verb "analyze" appears in both the CLO and the assessment tasks</li>
                        <li>Teaching activities provide practice in the same skills being assessed</li>
                        <li>Assessment weightage reflects the importance of this outcome in the course</li>
                        <li>SLT allocation ensures sufficient time for students to develop the required skills</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Benefits and Implementation Tips */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Benefits and Implementation Tips</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2">Benefits of Constructive Alignment</h4>
                      <ul className="text-green-800 text-sm space-y-1 list-disc list-inside">
                        <li>Increases transparency in the learning process</li>
                        <li>Promotes deep learning rather than surface learning</li>
                        <li>Ensures assessments truly measure the intended outcomes</li>
                        <li>Creates coherence across the curriculum</li>
                        <li>Improves student satisfaction and performance</li>
                        <li>Facilitates continuous improvement in teaching</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Implementation Tips</h4>
                      <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
                        <li>Start with clear, measurable learning outcomes</li>
                        <li>Use action verbs from Bloom's taxonomy</li>
                        <li>Ensure assessment tasks directly measure the outcomes</li>
                        <li>Design teaching activities that provide practice in what is being assessed</li>
                        <li>Regularly review and adjust alignment</li>
                        <li>Collect student feedback on the alignment</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Summary */}
                <section className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Implementing Constructive Alignment in Your Assessment Plan</h3>
                  <p className="text-gray-700 text-sm mb-3">
                    When creating your Assessment Plan, ensure that each assessment item:
                  </p>
                  <ul className="text-gray-700 text-sm space-y-2 list-decimal list-inside mb-4">
                    <li>Is explicitly linked to one or more CLOs</li>
                    <li>Uses assessment methods that appropriately measure the skills described in the CLO</li>
                    <li>Has a weightage that reflects the importance of the CLO</li>
                    <li>Is supported by teaching and learning activities that develop the required skills</li>
                    <li>Has appropriate SLT allocation to allow students to achieve the outcome</li>
                  </ul>
                  <button
                    onClick={() => setShowAlignmentGuide(false)}
                    className="mt-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Return to Assessment Plan
                  </button>
                </section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CLO Guide Modal */}
      <AnimatePresence>
        {showCLOGuide && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCLOGuide(false)}
          >
            <motion.div
              initial={{scale: 0.95, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.95, opacity: 0}}
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Understanding CLOs and Educational Outcomes</h2>
                <button
                  onClick={() => setShowCLOGuide(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Key Definitions */}
                <section className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Key Definitions</h3>
                  <div className="space-y-4">
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-1">Programme Educational Objective (PEO)</h4>
                      <p className="text-blue-700 text-sm">
                        A statement to describe the educational purpose of the offering of the programme in the form of
                        expected careers or capabilities of the profession.
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-1">Programme Learning Outcome (PLO)</h4>
                      <p className="text-blue-700 text-sm">
                        Desired ability or abilities needed to meet the PEOs, including abilities to apply knowledge, skills and attitudes.
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-1">Course Learning Outcome (CLO)</h4>
                      <p className="text-blue-700 text-sm">
                        Desired ability or abilities needed to meet the PLOs. CLOs are more conveniently mapped to the PLOs instead of listing all course outcomes.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Writing Educational Objectives */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Write an Educational Objective (PEO) Statement</h3>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="space-y-3 text-sm">
                      <div className="bg-white p-3 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-1">Step 1: Write a condition</h4>
                        <p className="text-green-700">
                          "The educational objective is to produce graduates after 5 years of graduation..."
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-1">Step 2: Write the expectation</h4>
                        <p className="text-green-700">
                          "...pursue career in 'XXX' profession" <strong>OR</strong> "...will demonstrate technical competency and leadership to become professional 'XXX'..."
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-1">Step 3: Write the criterion for success</h4>
                        <p className="text-green-700">
                          "...at a senior level position" <strong>OR</strong> "...leading to a successful career."
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Writing Learning Outcomes */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Write a Learning Outcome Statement</h3>
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <div className="space-y-3 text-sm">
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-800 mb-1">Step 1: Write a condition</h4>
                        <p className="text-indigo-700">
                          "After completing a programme/course/lesson, a graduate/student should be able to..."
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-800 mb-1">Step 2: Write a specific and measurable action verb</h4>
                        <p className="text-indigo-700">
                          Based on Bloom's taxonomies of cognitive, psychomotor and affective (CPA) domains or knowledge, skills, attitude (KSA) domain.
                        </p>
                        <p className="text-indigo-700 mt-1">
                          Example: "...investigate..."
                        </p>
                        <div className="bg-indigo-50 p-2 mt-2 rounded">
                          <p className="text-indigo-800 text-xs italic">
                            Avoid abstract verbs like 'understand', 'appreciate', 'know about', 'become familiar with', 'learn about', and 'become aware'.
                          </p>
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-800 mb-1">Step 3: Write the subject matter</h4>
                        <p className="text-indigo-700">
                          Related to the CPA or KSA domain, whichever appropriate.
                        </p>
                        <p className="text-indigo-700 mt-1">
                          Example: "...complex engineering problem..."
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-800 mb-1">Step 4: Write the criterion for success</h4>
                        <p className="text-indigo-700">
                          Indicate the scope or level of the attainment of the learning outcomes.
                        </p>
                        <p className="text-indigo-700 mt-1">
                          Example: "...using research-based knowledge and research methods including design of experiments, analysis and interpretation of data, and synthesis of information to provide valid conclusions."
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* SMART Principles */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">SMART Objectives and Learning Outcomes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <h4 className="font-semibold text-yellow-900 mb-2">Specific & Measurable</h4>
                      <p className="text-yellow-800 text-sm">
                        Use action verbs so learners' achievement can be directly observed from written, verbal, and non-verbal communication.
                      </p>
                      <div className="mt-2 bg-white p-2 rounded border border-yellow-200">
                        <p className="text-yellow-800 text-xs font-medium">Good Example:</p>
                        <p className="text-yellow-700 text-xs">"Analyze complex engineering problems using appropriate techniques"</p>
                        <p className="text-yellow-800 text-xs font-medium mt-1">Avoid:</p>
                        <p className="text-yellow-700 text-xs">"Understand engineering problems"</p>
                      </div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-2">Achievable</h4>
                      <p className="text-orange-800 text-sm">
                        Consider prerequisites to learn new knowledge, skills, and attitudes. Create a sequence of learning progression by linking the learning outcomes between courses.
                      </p>
                      <div className="mt-2 bg-white p-2 rounded border border-orange-200">
                        <p className="text-orange-800 text-xs font-medium">Example:</p>
                        <p className="text-orange-700 text-xs">
                          Basic concepts in Course 1 → Application in Course 2 → Analysis in Course 3
                        </p>
                      </div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <h4 className="font-semibold text-red-900 mb-2">Relevant</h4>
                      <p className="text-red-800 text-sm">
                        Ensure objectives and outcomes can be mapped 'justifiably' to stakeholders' needs:
                      </p>
                      <ul className="text-red-800 text-xs mt-2 space-y-1 list-disc list-inside">
                        <li>Map PEOs to the institution's vision and mission</li>
                        <li>Map PLOs to the PEOs and MQF learning outcome domains</li>
                        <li>Map CLOs to the PLOs</li>
                        <li>Map lesson outcomes to the CLOs</li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-2">Time-bound</h4>
                      <p className="text-purple-800 text-sm">
                        Specify when the outcome should be achieved:
                      </p>
                      <ul className="text-purple-800 text-xs mt-2 space-y-1 list-disc list-inside">
                        <li>CLOs: By the end of the course</li>
                        <li>PLOs: By the end of the programme</li>
                        <li>PEOs: Within 5 years after graduation</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Mapping Best Practices */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Mapping Best Practices</h3>
                  <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                    <p className="text-teal-800 text-sm mb-3">
                      The best practice is to have a one-to-one mapping between a lower-level item and a higher-level item and avoid mapping one lower-level item to many higher-level items.
                    </p>
                    <p className="text-teal-800 text-sm mb-3">
                      This ensures the lower-level item has direct relevance/correlation to the higher-level item, making it easier to measure the attainment of the higher-level item based on the attainment of the lower-level item.
                    </p>
                    <div className="bg-white p-3 rounded-lg border border-teal-200">
                      <h4 className="font-semibold text-teal-900 mb-1">Example of Good Mapping</h4>
                      <p className="text-teal-700 text-sm">
                        CLO1 → PLO3 (direct correlation)
                      </p>
                      <h4 className="font-semibold text-teal-900 mb-1 mt-2">Example to Avoid</h4>
                      <p className="text-teal-700 text-sm">
                        CLO1 → PLO3, PLO5, PLO7, PLO9 (difficult to measure contribution)
                      </p>
                    </div>
                  </div>
                </section>

                {/* Student Learning Time */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Student Learning Time (SLT)</h3>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-blue-800 text-sm mb-3">
                      The Malaysian credit value is 1 credit equivalent to 40 Notional Learning Hours (NLHs).
                    </p>
                    <div className="bg-white p-3 rounded-lg border border-blue-200 mb-3">
                      <h4 className="font-semibold text-blue-800 mb-1">SLT Calculation Example</h4>
                      <p className="text-blue-700 text-sm">
                        For a 3-credit hour course in a typical 17-week semester:
                      </p>
                      <p className="text-blue-700 text-sm font-mono mt-1">
                        Total SLT = 3 credits × 40 hours = 120 hours
                      </p>
                      <p className="text-blue-700 text-sm font-mono">
                        Weekly SLT = 3 credits × 40 hours / 17 weeks ≈ 7 hours
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-1">Guided Learning</h4>
                        <p className="text-blue-700 text-xs">
                          Structured learning with set outcomes and delivery methods:
                        </p>
                        <ul className="text-blue-700 text-xs mt-1 space-y-1 list-disc list-inside">
                          <li>Face-to-face activities (lectures, tutorials, labs)</li>
                          <li>Non-face-to-face activities (online learning)</li>
                          <li>Blended learning, project-based learning, etc.</li>
                        </ul>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-1">Independent Learning</h4>
                        <p className="text-blue-700 text-xs">
                          Non-structured learning related to outcomes but conducted voluntarily by students:
                        </p>
                        <ul className="text-blue-700 text-xs mt-1 space-y-1 list-disc list-inside">
                          <li>Preparation for guided learning activities</li>
                          <li>Revision of past lessons</li>
                          <li>Self-study and additional practice</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Teaching-Learning Method */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Planning Teaching-Learning Methods</h3>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-green-800 text-sm mb-3">
                      Planning for the appropriate teaching-learning method should follow the principles of <strong>constructive alignment</strong>. Teachers must make the assessment and delivery explicitly conform to ensure learning takes place.
                    </p>
                    <div className="bg-white p-3 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-1">Constructive Alignment Example</h4>
                      <div className="text-green-700 text-xs space-y-1">
                        <p>
                          <strong>CLO:</strong> "Analyze complex engineering problems"
                        </p>
                        <p>
                          <strong>Teaching Method:</strong> Case studies that require analysis
                        </p>
                        <p>
                          <strong>Assessment:</strong> Project requiring analysis of a complex problem
                        </p>
                      </div>
                      <p className="text-green-700 text-xs mt-2 italic">
                        Note: The verb "analyze" appears in all three components, creating alignment.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Semester System Policy */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">MOHE's Policy on Semester System</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
                    <ul className="text-gray-800 text-sm space-y-2">
                      <li>1. 1 study year = maximum 43 weeks.</li>
                      <li>2. Maximum number of session/semester in a study year is 3 semesters.</li>
                      <li>3. Maximum study load for a student in a study year is 50 credits.</li>
                      <li>4. The maximum credit for a semester is 20 credits*.</li>
                      <li>5. Student intake session is based on the number of session/semester in a year.</li>
                      <li>6. The minimum programme period is based on MOHE policy.</li>
                    </ul>
                    <p className="text-gray-600 text-xs mt-3">
                      *except for foundation and master's courses.
                    </p>
                    <p className="text-gray-600 text-xs">
                      Note: The calculation of 20 credits maximum per semester is based on an SLT calculation of a 17-week study in one (1) semester. The maximum credit will be less if the HEP uses a semester system that is less than 17 study weeks.
                    </p>
                  </div>
                </section>

                {/* Summary */}
                <section className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Remember When Creating Your Assessment Plan</h3>
                  <ul className="text-gray-700 text-sm space-y-2">
                    <li>1. Ensure CLOs are SMART (Specific, Measurable, Achievable, Relevant, Time-bound)</li>
                    <li>2. Map each assessment to an appropriate CLO</li>
                    <li>3. Calculate SLT based on credit hours (1 credit = 40 hours)</li>
                    <li>4. Ensure constructive alignment between CLOs, teaching methods, and assessments</li>
                    <li>5. Balance guided and independent learning components</li>
                    <li>6. Total assessment marks should equal 100%</li>
                  </ul>
                  <button
                    onClick={() => setShowCLOGuide(false)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Return to Assessment Plan
                  </button>
                </section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default AssessmentPlan